import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import ClearIcon from '@mui/icons-material/Clear';
import ServerIcon from '@mui/icons-material/Storage';
import KeyIcon from '@mui/icons-material/VpnKey';
import {
  Badge,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Tooltip,
} from '@mui/material';

import {
  clearEnvironmentOverride,
  getEnvironmentOverride,
  setEnvironmentFromKey,
  StateWithEnvironment,
} from '@paragon/environment-tools';

import { isReduxDevToolsSetup } from '../../index';
import { isInDevToolsEnvironment } from '../environments/isInDevToolsEnvironment';
import { isReduxDevToolsEnabled, toggleReduxDevTools } from '../utilities/cookies';
import { reloadPage } from '../utilities/reloadPage';
import EnvironmentDevTools from './panels/EnvironmentDevTools';
import PermissionsDevTools from './panels/PermissionsDevTools';

type DevToolsPanel = 'permissions' | 'environments' | 'environment';

export interface DevToolsProps {
  readonly client?: 'mobile' | 'desktop';
  readonly sx?: SxProps<Theme>;
}

export default function DevToolsButton(props: DevToolsProps) {
  const dispatch = useDispatch();

  const activeEnvironment = useSelector((state: StateWithEnvironment) => state.environment.activeEnvironment);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = !!menuAnchor;

  const [panelOpen, setPanelOpen] = useState<DevToolsPanel | null>(null);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const openPanel = (panel: DevToolsPanel) => {
    closeMenu();
    setPanelOpen(panel);
  };

  const closePanel = () => {
    setPanelOpen(null);
  };

  if (!isInDevToolsEnvironment()) {
    return null;
  }

  const devToolMenuItems = [];
  const resetHandlers: { needsRefresh: boolean; apply: () => void }[] = [];

  devToolMenuItems.push(
    <MenuItem key="permissions" onClick={() => openPanel('permissions')}>
      <KeyIcon sx={{ mr: 1 }} />
      Edit user permissions
    </MenuItem>,
  );

  if (isReduxDevToolsSetup()) {
    const enabled = isReduxDevToolsEnabled();
    if (enabled) {
      resetHandlers.push({
        needsRefresh: true,
        apply: () => toggleReduxDevTools(false),
      });
    }

    const reduxAction = enabled ? 'Disable' : 'Enable';

    devToolMenuItems.push(
      <MenuItem onClick={() => toggleReduxDevTools()} key="redux">
        <AssignmentIcon sx={{ mr: 1 }} />
        {`${reduxAction} redux devtools`}
      </MenuItem>,
    );
  }

  if (process.env.NODE_ENV !== 'production') {
    const environmentOverride = getEnvironmentOverride();
    if (environmentOverride && environmentOverride !== process.env.APP_ENV) {
      resetHandlers.push({
        needsRefresh: true,
        apply: () => clearEnvironmentOverride(false),
      });
    } else if (activeEnvironment !== process.env.APP_ENV) {
      resetHandlers.push({
        needsRefresh: false,
        apply: () => {
          dispatch(setEnvironmentFromKey(process.env.APP_ENV!));
          closeMenu();
        },
      });
    }

    devToolMenuItems.push(
      <MenuItem key="environment" onClick={() => openPanel('environment')}>
        <ServerIcon sx={{ mr: 1 }} />
        Change server environment
      </MenuItem>,
    );
  }

  let openDevToolsPanel;
  switch (panelOpen) {
    case 'permissions':
      openDevToolsPanel = <PermissionsDevTools onClose={closePanel} />;
      break;

    case 'environment':
      openDevToolsPanel = <EnvironmentDevTools onClose={closePanel} />;
      break;

    // no default
  }

  if (devToolMenuItems.length > 0 && resetHandlers.length > 0) {
    devToolMenuItems.push(<Divider key="divider" />);
    devToolMenuItems.push(
      <MenuItem
        key="reset"
        onClick={() => {
          let needsRefresh = false;

          for (const handler of resetHandlers) {
            if (handler.needsRefresh) {
              needsRefresh = true;
            }

            handler.apply();
          }

          if (needsRefresh) {
            reloadPage();
          }
        }}
      >
        <ClearIcon sx={{ mr: 1 }} />
        Reset changes
      </MenuItem>,
    );
  }

  if (devToolMenuItems.length === 0) {
    return null;
  }

  let icon = <BuildIcon sx={props.client === 'mobile' ? { mr: 1, color: '#fffff' } : { color: '#ffffff' }} />;
  if (resetHandlers.length > 0) {
    icon = (
      <Badge badgeContent="!" color="error">
        {icon}
      </Badge>
    );
  }

  const button =
    props.client === 'mobile' ? (
      <MenuItem sx={props.sx} onClick={openMenu}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText>Developer Tools</ListItemText>
      </MenuItem>
    ) : (
      <Tooltip disableInteractive title="Developer Tools">
        <IconButton sx={props.sx} onClick={openMenu}>
          {icon}
        </IconButton>
      </Tooltip>
    );

  return (
    <>
      {button}

      <Menu open={menuOpen} anchorEl={menuAnchor} onClose={closeMenu}>
        {devToolMenuItems}
      </Menu>

      {openDevToolsPanel}
    </>
  );
}
